import type { FormVariant } from '@/components/pages/form/type';
import type { FooterProps } from './type';

/**
 * A distinct `data-form-variant` for the footer form, so its CSS (scoped to `.footer-form`) never
 * inherits the variables a `default`-themed form on the same page would set. No variant chunk is
 * registered — the look is all local CSS.
 */
export const FOOTER_FORM_VARIANT = 'footer' as unknown as FormVariant;

/**
 * Scroll anchor for the navbar's "Free case review" CTA. The card streams a skeleton and
 * swaps in the hydrated engine, replacing the inputs inside it — this wrapper survives that
 * swap, so it is what `useNavbar` aims at. See JLG4-239.
 */
export const FOOTER_FORM_ANCHOR_ID = 'footer-lead-form';

export const companyLinks = [
  { label: 'About', href: '#' },
  { label: 'Our team', href: '#' },
  { label: 'Careers', href: '#', badge: "We're hiring!" },
  { label: 'Frequently asked questions', href: '#' },
  { label: 'Privacy policy', href: 'https://johnsonlawgroup.com/legal/privacy-policy' },
  { label: 'Disclaimer', href: '#' },
];

export const social = [
  { icon: 'facebook', href: 'https://www.facebook.com/JohnsonLawGroup/' },
  { icon: 'x', href: 'https://x.com/JohnsonLawGrp' },
  { icon: 'linkedin', href: 'https://www.linkedin.com/company/johnson-law-group/' },
  { icon: 'instagram', href: 'https://www.instagram.com/johnsonlawgroupus/' },
];

export const contacts = [
  { label: 'info@johnsonlawgroup.com', href: 'mailto:info@johnsonlawgroup.com' },
  { label: 'Phone: (713) 626-9336', href: 'tel:+17136269336' },
  { label: 'Toll free: (800) 624-8899', href: 'tel:+18006248899' },
  { label: 'Fax: (800) 731-6018', href: 'tel:+18007316018' },
];

export const locations = [
  {
    city: 'Houston',
    address: '2925 Richmond Avenue, Ste 1700',
    cityState: 'Houston, TX 77098',
    slug: '/texas/houston',
  },
  {
    city: 'Chicago',
    address: '1 S Dearborn St Ste 1520',
    cityState: 'Chicago, IL 60603',
    slug: '/illinois/chicago',
  },
  {
    city: 'New York',
    address: '17 State St, Ste 2020',
    cityState: 'New York, NY 10004',
    slug: '/new-york/new-york',
  },
  {
    city: 'Nashville',
    address: '1019 16th Ave S',
    cityState: 'Nashville, TN 37212',
    slug: '/tennessee/nashville',
  },
  {
    city: 'Cleveland',
    address: '1468 W 9th St Suite #100',
    cityState: 'Cleveland, OH 44113',
    slug: '/ohio/cleveland',
  },
  {
    city: 'Seattle',
    address: '701 5th Avenue, Suite 4200',
    cityState: 'Seattle, WA 98104',
    slug: '/washington/seattle',
  },
  {
    city: 'London, UK',
    address: '20 Gracechurch Street,',
    cityState: 'London, EC3V 0BG',
    slug: '/uk/london',
  },
  {
    city: 'Manchester, UK',
    address: 'Chancery Pl, 50 Brown St',
    cityState: 'Manchester M2 2JT',
    slug: '/uk/manchester',
  },
];

export const defaultValue: Omit<FooterProps, 'claimSections'> = {
  footerBackgroundImage: '/images/nh-team.webp',
  footerPhrase: `We're at your service<br class="block @lg:hidden" /> <i>when it matters most</i>`,

  showClaimSectionsLinks: false,
  claimSectionsTitle: 'Practice areas',

  showLinks: false,
  linksTitle: 'Company',
  links: companyLinks,

  showLocations: true,
  locationsTitle: 'Locations',
  locations,

  showContacts: true,
  contactsTitle: 'Get in touch',
  contacts,

  operatingHours: 'Available 24/7',

  showSocial: true,
  social,
};
